import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, forwardRef, ViewChild} from '@angular/core';
import * as _ from 'lodash-es';
import {CustomScrollbarComponent, Ending} from '../custom-scrollbar/custom-scrollbar.component';
import {SCROLLBAR, ScrollingAxis} from '../custom-scrollbar/scrollbar';

interface ScrollEndingStyleInfo {
    hiddenClass: string;
    hidingRatioVariable: string;
}

const SCROLL_ENDINGS_STYLE_INFO_DICT: {
    [axis in ScrollingAxis]: {
        [ending in Ending]: ScrollEndingStyleInfo;
    }
} = {
    [ScrollingAxis.X]: {
        [Ending.START]: {
            hiddenClass:         '-hidden-left',
            hidingRatioVariable: '--scrollLeftEndingHidingRatio'
        },
        [Ending.END]:   {
            hiddenClass:         '-hidden-right',
            hidingRatioVariable: '--scrollRightEndingHidingRatio'
        }
    },
    [ScrollingAxis.Y]:   {
        [Ending.START]: {
            hiddenClass:         '-hidden-top',
            hidingRatioVariable: '--scrollTopEndingHidingRatio'
        },
        [Ending.END]:   {
            hiddenClass:         '-hidden-bottom',
            hidingRatioVariable: '--scrollBottomEndingHidingRatio'
        }
    }
};

type ScrollEndingsHidingNumberDict = {
    [axis in ScrollingAxis]: {
        [ending in Ending]: number;
    }
};

const EMPTY_SCROLL_ENDINGS_HIDING_NUMBER_DICT: ScrollEndingsHidingNumberDict = {
    [ScrollingAxis.X]: {
        [Ending.START]: null,
        [Ending.END]:   null
    },
    [ScrollingAxis.Y]:   {
        [Ending.START]: null,
        [Ending.END]:   null
    }
};

@Component({
    selector: '[scrollbarWithEndingBlinds]',
    templateUrl: './scrollbar-with-ending-blinds.component.html',
    styleUrls: ['./scrollbar-with-ending-blinds.component.scss'],
    providers: [
        {
            provide: SCROLLBAR,
            useExisting: forwardRef(() => ScrollbarWithEndingBlindsComponent)
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ScrollbarWithEndingBlindsComponent extends CustomScrollbarComponent implements AfterViewInit {

    @ViewChild('topEndingBlind', {static: true})
    public topEndingBlindElement: ElementRef;

    @ViewChild('bottomEndingBlind', {static: true})
    public bottomEndingBlindElement: ElementRef;

    @ViewChild('leftEndingBlind', {static: true})
    public leftEndingBlindElement: ElementRef;

    @ViewChild('rightEndingBlind', {static: true})
    public rightEndingBlindElement: ElementRef;

    private containerSize: ClientRect;

    private scrollEndingBlindsLengthDict: ScrollEndingsHidingNumberDict =
                _.cloneDeep(EMPTY_SCROLL_ENDINGS_HIDING_NUMBER_DICT);

    private scrollEndingsHidingRatioDict: ScrollEndingsHidingNumberDict =
                _.cloneDeep(EMPTY_SCROLL_ENDINGS_HIDING_NUMBER_DICT);

    public override ngAfterViewInit(): void {
        this.updateScrollEndingBlindsLength();

        super.ngAfterViewInit();
    }

    protected override updateBars(): void {
        this.containerSize = null;

        super.updateBars();
    }

    protected override updateScrollEndingsVisibility(
        axis: ScrollingAxis,
        {isBothVisible, scrollLength, scrollOffset}: {
            isBothVisible?: boolean;
            scrollLength?: number;
            scrollOffset?: number;
        }
    ): void {
        let endingsHidingRatio: [number, number];

        if (isBothVisible === true || isBothVisible === false) {
            endingsHidingRatio = [0, 0];
        } else {
            this.invalidateContainerSize();

            const containerLength = axis === ScrollingAxis.X
                ? this.containerSize.width - this.customScrollbarService.hiddenScrollbarWidth
                : this.containerSize.height - this.customScrollbarService.hiddenScrollbarWidth;

            const endScrollOffset = scrollLength - scrollOffset - containerLength;

            endingsHidingRatio = [
                scrollOffset > 0
                    ? this.calcScrollEndingHidingRatio(axis, Ending.START, scrollOffset)
                    : 0,
                endScrollOffset > 0
                    ? this.calcScrollEndingHidingRatio(axis, Ending.END, endScrollOffset)
                    : 0
            ];
        }

        this.setScrollEndingHidingRatio(axis, Ending.START, endingsHidingRatio[Ending.START]);
        this.setScrollEndingHidingRatio(axis, Ending.END, endingsHidingRatio[Ending.END]);
    }

    protected override getScrollToOffsetOnAxis(axis: ScrollingAxis, ending: Ending): number {
        return this.scrollEndingsHidingRatioDict[axis][ending]
            ? this.scrollEndingBlindsLengthDict[axis][ending]
            : 0;
    }

    private setScrollEndingHidingRatio(axis: ScrollingAxis, ending: Ending, hidingRatio: number): void {
        const styleInfo = SCROLL_ENDINGS_STYLE_INFO_DICT[axis][ending];
        const stylesTargetElement: HTMLElement = this.wrapperElement.nativeElement;

        this.scrollEndingsHidingRatioDict[axis][ending] = hidingRatio;

        if (!hidingRatio) {
            this.renderer.removeClass(stylesTargetElement, styleInfo.hiddenClass);
        } else {
            this.renderer.addClass(stylesTargetElement, styleInfo.hiddenClass);

            stylesTargetElement.style.setProperty(styleInfo.hidingRatioVariable, String(hidingRatio));
        }
    }

    private invalidateContainerSize(): void {
        this.containerSize = this.containerSize
            || (this.containerElement.nativeElement as HTMLElement).getBoundingClientRect();
    }

    private calcScrollEndingHidingRatio(axis: ScrollingAxis, ending: Ending, scrollOffset: number): number {
        const MAX_HIDING_RATIO = 1; // Max possible opacity.
        const HIDING_SPEED = 3; // An empirically picked value.

        const blindLength = this.scrollEndingBlindsLengthDict[axis][ending] || 0;
        const maxOffset = blindLength / HIDING_SPEED;

        return maxOffset
            ? Math.min(scrollOffset, maxOffset) / maxOffset
            : MAX_HIDING_RATIO;
    }

    private updateScrollEndingBlindsLength(): void {
        this.scrollEndingBlindsLengthDict = {
            [ScrollingAxis.X]: {
                [Ending.START]: (this.leftEndingBlindElement.nativeElement as HTMLElement).clientWidth,
                [Ending.END]:   (this.rightEndingBlindElement.nativeElement as HTMLElement).clientWidth
            },
            [ScrollingAxis.Y]:   {
                [Ending.START]: (this.topEndingBlindElement.nativeElement as HTMLElement).clientHeight,
                [Ending.END]:   (this.bottomEndingBlindElement.nativeElement as HTMLElement).clientHeight
            }
        };
    }

}
